import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ProtocolPhotoGallery } from "@/components/equipment/ProtocolPhotoGallery";
import { protocolDirectionLabel } from "@/components/equipment/equipmentLabels";
import type { EquipmentProtocol } from "@/types/equipment";

interface ProtocolReviewDialogProps {
  protocol: EquipmentProtocol | null;
  itemName: string;
  serialNumber: string | null;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: () => void;
  onReject: (reason: string) => void;
}

export function ProtocolReviewDialog({
  protocol,
  itemName,
  serialNumber,
  saving,
  onOpenChange,
  onAccept,
  onReject,
}: ProtocolReviewDialogProps) {
  const { t } = useTranslation();
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");

  const resetAndClose = (open: boolean) => {
    if (!open) {
      setRejectMode(false);
      setReason("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={Boolean(protocol)} onOpenChange={resetAndClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {protocol ? protocolDirectionLabel(protocol.direction) : t("admin.equipment.review.protocolFallback")}
          </DialogTitle>
          <DialogDescription>
            {t("admin.equipment.review.checkItem")}
          </DialogDescription>
        </DialogHeader>
        {protocol && (
          <div className="space-y-4 py-2">
            <div>
              <p className="text-xs text-muted-foreground">{t("admin.equipment.fields.item")}</p>
              <p className="font-medium">{itemName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("admin.equipment.fields.serialNumber")}</p>
              <p className="font-medium">{serialNumber || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("admin.equipment.review.coordinatorCondition")}</p>
              <p className="whitespace-pre-wrap text-sm">
                {protocol.condition_notes?.trim() || t("admin.equipment.review.noCondition")}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{t("admin.equipment.fields.photos")}</p>
              <ProtocolPhotoGallery paths={protocol.photo_urls ?? []} />
            </div>
            {rejectMode && (
              <div className="space-y-2">
                <Label htmlFor="equipment-reject-reason">{t("admin.equipment.review.rejectionReason")}</Label>
                <Textarea
                  id="equipment-reject-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("admin.equipment.review.rejectPlaceholder")}
                  maxLength={2000}
                />
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => {
              if (rejectMode) {
                setRejectMode(false);
                setReason("");
                return;
              }
              resetAndClose(false);
            }}
            disabled={saving}
          >
            {rejectMode ? t("common.back") : t("common.cancel")}
          </Button>
          {rejectMode ? (
            <Button
              variant="destructive"
              disabled={saving || !reason.trim()}
              onClick={() => onReject(reason.trim())}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("admin.equipment.review.confirmReject")}
            </Button>
          ) : (
            <>
              <Button variant="destructive" disabled={saving} onClick={() => setRejectMode(true)}>
                {t("admin.equipment.review.reject")}
              </Button>
              <Button disabled={saving} onClick={onAccept}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("admin.equipment.review.accept")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
